/**
 * Variant Specifications Handler
 * 
 * Dynamically updates product specification values when variant changes.
 * Uses the fresh HTML from the theme's variant change event to extract
 * and update specification values without page refresh.
 * 
 * @version 2.2 - Supports multiple colors per variant
 */

(function() {
  'use strict';

  let isInitialized = false;
  let initAttempts = 0;
  const MAX_INIT_ATTEMPTS = 50;

  /**
   * Initialize the variant specifications handler
   */
  function init() {
    initAttempts++;
    
    // Check if FoxTheme is ready
    if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub || !FoxTheme.pubsub.PUB_SUB_EVENTS) {
      if (initAttempts < MAX_INIT_ATTEMPTS) {
        setTimeout(init, 100);
      } else {
        console.warn('[VariantSpecs] FoxTheme not available after max attempts, falling back to DOM events only');
        setupDOMEventListeners();
      }
      return;
    }

    if (isInitialized) return;
    isInitialized = true;

    console.log('[VariantSpecs] Initializing with FoxTheme pubsub');

    // Subscribe to variant change event (this is the main handler)
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, handlePubSubVariantChange);

    // Also set up DOM event listeners as backup
    setupDOMEventListeners();

    console.log('[VariantSpecs] Initialization complete');
  }

  /**
   * Set up DOM event listeners
   */
  function setupDOMEventListeners() {
    // Listen for variant:changed event on document
    document.addEventListener('variant:changed', handleDOMVariantChange);
  }

  /**
   * Handle pubsub variant change event
   * This is the primary handler - it receives fresh HTML with updated values
   */
  function handlePubSubVariantChange(event) {
    const { sectionId, html, variant } = event.data || {};
    
    if (!variant || !html) {
      console.log('[VariantSpecs] Missing variant or html in pubsub event');
      return;
    }

    console.log('[VariantSpecs] PubSub variantChange:', variant.id, 'sectionId:', sectionId);

    // Update specifications from the fresh HTML
    updateSpecificationsFromHTML(sectionId, html, variant);
  }

  /**
   * Handle DOM variant:changed event (backup)
   */
  function handleDOMVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    console.log('[VariantSpecs] DOM variant:changed:', variant.id);

    // Direct update of SKU and Barcode from variant object
    updateBasicFieldsFromVariant(variant);
  }

  /**
   * Update specifications from fresh HTML
   * This is the main update function that extracts values from the fetched HTML
   */
  function updateSpecificationsFromHTML(sectionId, html, variant) {
    // List of specification fields to update (cell IDs without the -Row suffix)
    const specFields = [
      { name: 'Farve', isHtml: true },  // Farve uses HTML for swatches
      { name: 'Sku', isHtml: false },
      { name: 'Barcode', isHtml: false },
      { name: 'Materiale', isHtml: false },
      { name: 'Dimensioner', isHtml: false },
      { name: 'Vaegt', isHtml: false },
      { name: 'Storrelse', isHtml: false },
      { name: 'AnbefaletAlder', isHtml: false },
      { name: 'Indholdsvolume', isHtml: false },
      { name: 'Antal', isHtml: false }
    ];

    specFields.forEach(field => {
      updateSpecFieldFromHTML(field.name, sectionId, html, field.isHtml);
    });

    // Also directly update SKU and Barcode from variant object (most reliable for these)
    updateBasicFieldsFromVariant(variant, sectionId);
  }

  /**
   * Update a single specification field from fresh HTML
   * IMPORTANT: Only targets td cells, NOT tr rows
   */
  function updateSpecFieldFromHTML(fieldName, sectionId, html, preserveHtml = false) {
    // Build the exact cell ID (not row ID)
    const cellId = `Spec-${fieldName}-${sectionId}`;
    
    // Find source cell in fresh HTML by exact ID
    const source = html.getElementById(cellId);
    
    // Find destination cell on the page by exact ID
    const destination = document.getElementById(cellId);

    if (source && destination) {
      // Only update if it's actually a td element (safety check)
      if (destination.tagName === 'TD') {
        if (preserveHtml) {
          destination.innerHTML = source.innerHTML;
        } else {
          destination.textContent = source.textContent;
        }
        console.log(`[VariantSpecs] Updated ${fieldName} from HTML${preserveHtml ? ' (with HTML)' : ''}:`, 
          source.textContent.substring(0, 50));
      }
      
      // Update row visibility based on source row
      const sourceRow = source.closest('tr');
      const destRow = destination.closest('tr');
      if (sourceRow && destRow) {
        destRow.style.display = sourceRow.style.display || '';
      }
    }
  }

  /**
   * Update basic fields (SKU, Barcode) directly from variant object
   * IMPORTANT: Only targets td cells, NOT tr rows
   */
  function updateBasicFieldsFromVariant(variant, sectionId) {
    if (!variant) return;

    // Update SKU - use exact ID selector to avoid matching rows
    updateSingleField('Sku', variant.sku, sectionId);
    
    // Update Barcode
    updateSingleField('Barcode', variant.barcode, sectionId);

    // Update option-based fields (size, material) from variant.options
    if (variant.options && Array.isArray(variant.options)) {
      // Only target td elements with the spec-option-value class
      document.querySelectorAll('td.spec-option-value[data-option-index]').forEach(cell => {
        const optionIndex = parseInt(cell.dataset.optionIndex, 10);
        if (!isNaN(optionIndex) && variant.options[optionIndex] !== undefined) {
          cell.textContent = variant.options[optionIndex];
          console.log(`[VariantSpecs] Updated option ${optionIndex}:`, variant.options[optionIndex]);
        }
      });

      // Derive "Anbefalet alder" from the selected size option
      const sizeValue = variant.options.find(opt => {
        const lower = (opt || '').toLowerCase();
        return lower.includes('str') || lower.includes('0-6') || lower.includes('6+');
      });
      if (sizeValue) {
        const lower = sizeValue.toLowerCase();
        let age = '';
        if (lower.includes('0-6')) {
          age = '0-6 måneder';
        } else if (lower.includes('6+')) {
          age = '6+ måneder';
        }
        if (age) {
          document.querySelectorAll('td[id^="Spec-AnbefaletAlder-"]').forEach(el => {
            if (el.tagName === 'TD') {
              el.textContent = age;
              console.log('[VariantSpecs] Updated AnbefaletAlder from size:', age);
            }
          });
        }
      }
    }
  }

  /**
   * Update a single field by exact ID
   * Only updates td elements, never tr elements
   */
  function updateSingleField(fieldName, value, sectionId) {
    // Try with sectionId first
    if (sectionId) {
      const exactId = `Spec-${fieldName}-${sectionId}`;
      const el = document.getElementById(exactId);
      
      if (el && el.tagName === 'TD') {
        el.textContent = value || '';
        
        // Show/hide row based on whether there's a value
        const row = el.closest('tr');
        if (row) {
          row.style.display = value ? '' : 'none';
        }
        console.log(`[VariantSpecs] Updated ${fieldName} by exact ID:`, value);
        return;
      }
    }

    // Fallback: find all td elements with matching ID pattern (but NOT rows)
    document.querySelectorAll(`td[id^="Spec-${fieldName}-"]`).forEach(el => {
      if (el.tagName === 'TD') {
        el.textContent = value || '';
        
        const row = el.closest('tr');
        if (row) {
          row.style.display = value ? '' : 'none';
        }
        console.log(`[VariantSpecs] Updated ${fieldName} by pattern:`, value);
      }
    });
  }

  // Start initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for debugging
  window.VariantSpecs = {
    init,
    updateBasicFieldsFromVariant,
    isInitialized: () => isInitialized
  };

})();